<template>
  <div>
    <button
      v-if="!hideButton"
      @click="toggle"
      :class="{ active: isWindowOpen }"
      class="inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-btn-gradient rounded-lg transition-colors duration-200 hover:opacity-90 focus:outline-none focus:ring-1 focus:ring-primary"
      type="button"
    >
      Poll Scheduler
      <svg
        class="w-2.5 h-2.5 ms-3"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 10 6"
      >
        <path
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="m1 1 4 4 4-4"
        />
      </svg>
    </button>
    <div v-if="hideButton || isWindowOpen" class="scheduler-container">
      <div class="p-6">
        <!-- Header -->
        <div class="mb-6">
          <h3 class="text-xl font-semibold text-primary-dark-text mb-2">
            Repeat Settings
          </h3>
          <p class="text-sm text-neutral-light">
            Configure when your poll should repeat
          </p>
        </div>

        <!-- Repeat Frequency Selection -->
        <div class="mb-6">
          <label
            for="repeat-option"
            class="block text-sm font-medium text-neutral-text mb-3"
            >Repeat every:</label
          >
          <div class="relative">
            <select
              v-model="repeatOption"
              id="repeat-option"
              class="appearance-none block w-full px-4 py-3 border border-neutral-light rounded-lg bg-white text-neutral-text focus:ring-1 focus:ring-primary focus:border-primary outline-none transition-colors duration-200 pr-10"
            >
              <option value="every-x-days">Every Day</option>
              <option value="every-week">Every Week</option>
              <option value="every-bi-week">Every Second Week</option>
              <option value="every-month">Every Month</option>
            </select>
            <svg
              class="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
        <!-- Time Selection -->
        <div class="mb-6">
          <div class="mb-3">
            <h4 class="text-sm font-medium text-neutral-text">Select Time</h4>
          </div>
          <div class="flex justify-center items-center space-x-3">
            <div class="relative">
              <input
                v-model="hourInput"
                @input="validateHour"
                @blur="formatHour"
                type="text"
                maxlength="2"
                placeholder="12"
                class="w-16 h-12 text-center text-lg font-medium bg-gray-50 border border-neutral-light rounded-lg focus:ring-1 focus:ring-primary focus:border-primary outline-none transition-colors duration-200"
                :class="{
                  'border-red-300 focus:ring-red-200 focus:border-red-400':
                    hourError,
                }"
              />
              <div
                v-if="hourError"
                class="absolute -bottom-5 left-0 text-xs text-red-500"
              >
                0-23
              </div>
            </div>
            <span class="text-2xl font-bold text-gray-400">:</span>
            <div class="relative">
              <input
                v-model="minuteInput"
                @input="validateMinute"
                @blur="formatMinute"
                type="text"
                maxlength="2"
                placeholder="00"
                class="w-16 h-12 text-center text-lg font-medium bg-gray-50 border border-neutral-light rounded-lg focus:ring-1 focus:ring-primary focus:border-primary outline-none transition-colors duration-200"
                :class="{
                  'border-red-300 focus:ring-red-200 focus:border-red-400':
                    minuteError,
                }"
              />
              <div
                v-if="minuteError"
                class="absolute -bottom-5 left-0 text-xs text-red-500"
              >
                0-59
              </div>
            </div>
          </div>
        </div>

        <!-- Weekly Selection -->
        <div v-if="repeatOption === 'every-week'" class="mb-6">
          <label class="block text-sm font-medium text-neutral-text mb-3">
            Days of the week:
          </label>
          <div class="grid grid-cols-2 gap-3">
            <label
              v-for="(day, index) in daysOfWeek"
              :key="index"
              class="flex items-center space-x-3 p-3 border border-neutral-light rounded-lg hover:bg-gray-50 cursor-pointer transition-colors duration-200"
              :class="{
                'border-primary/40 bg-primary/10 text-primary-neutral':
                  selectedWeekDays.includes(day),
              }"
            >
              <input
                type="checkbox"
                :value="day"
                v-model="selectedWeekDays"
                class="w-4 h-4 text-primary border-gray-300 rounded focus:ring-1 focus:ring-primary"
              />
              <span class="text-neutral-text font-medium">{{ day }}</span>
            </label>
          </div>
        </div>

        <!-- Bi-weekly Selection -->
        <div v-if="repeatOption === 'every-bi-week'" class="mb-6">
          <label class="block text-sm font-medium text-neutral-text mb-3">
            Days of the week:
          </label>
          <div class="grid grid-cols-2 gap-3 mb-4">
            <label
              v-for="(day, index) in daysOfWeek"
              :key="index"
              class="flex items-center space-x-3 p-3 border border-neutral-light rounded-lg hover:bg-gray-50 cursor-pointer transition-colors duration-200"
              :class="{
                'border-primary/40 bg-primary/10 text-primary-neutral':
                  selectedWeekDays.includes(day),
              }"
            >
              <input
                type="checkbox"
                :value="day"
                v-model="selectedWeekDays"
                class="w-4 h-4 text-primary border-gray-300 rounded focus:ring-1 focus:ring-primary"
              />
              <span class="text-neutral-text font-medium">{{ day }}</span>
            </label>
          </div>
          <div class="bg-primary/5 border border-primary/20 rounded-lg p-3">
            <div class="flex items-start">
              <svg
                class="w-4 h-4 text-primary-neutral mr-2 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div class="text-sm text-neutral-text">
                <span class="font-medium text-primary-neutral">Note:</span>
                <span class="text-neutral-text">
                  The poll will start next week on selected days, then repeat
                  every two weeks.</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Monthly Selection -->
        <div v-if="repeatOption === 'every-month'" class="mb-6">
          <label
            for="month-day"
            class="block text-sm font-medium text-neutral-text mb-3"
            >Day of the month:</label
          >
          <input
            type="number"
            v-model.number="monthDay"
            id="month-day"
            min="1"
            max="31"
            placeholder="Enter day (1-31)"
            class="block w-full px-4 py-3 border border-neutral-light rounded-lg bg-white text-neutral-text focus:ring-1 focus:ring-primary focus:border-primary outline-none transition-colors duration-200"
          />
          <div
            class="bg-primary/5 border border-primary/20 rounded-lg p-3 mt-3"
          >
            <div class="flex items-start">
              <svg
                class="w-4 h-4 text-primary-neutral mr-2 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <div class="text-sm text-neutral-text">
                <span class="font-medium text-primary-neutral">Note:</span>
                <span class="text-neutral-text">
                  If you choose a high date (like 31st) and some months don't
                  have that day, the poll will be sent on the last day of that
                  month.</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="mt-4">
          <div class="bg-red-50 border border-red-200 rounded-lg p-3">
            <div class="flex items-center">
              <svg
                class="w-4 h-4 text-red-400 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span class="text-sm text-red-700">{{ errorMessage }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "preline/preline";

export default {
  props: {
    hideButton: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isWindowOpen: this.hideButton,
      repeatOption: "every-x-days",
      selectedWeekDays: [],
      monthDay: 1,
      isSettingsApplied: false,
      daysOfWeek: [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ],
      selectedHour: 12,
      selectedMinute: "00",
      hourInput: "12",
      minuteInput: "00",
      hourError: false,
      minuteError: false,
      errorMessage: "",
    };
  },
  watch: {
    repeatOption() {
      this.errorMessage = "";
      this.autoApply();
    },
    selectedWeekDays: {
      handler() {
        this.autoApply();
      },
      deep: true,
    },
    monthDay() {
      this.autoApply();
    },
  },
  methods: {
    toggle() {
      this.isWindowOpen = !this.isWindowOpen;
      this.$emit("isWindowOpen", this.isWindowOpen);
      if (!this.isWindowOpen) {
        this.applySettings({ closeOnSuccess: false });
      }
    },
    autoApply() {
      this.applySettings({ closeOnSuccess: false });
    },
    validateHour() {
      const hour = parseInt(this.hourInput);
      this.hourError = isNaN(hour) || hour < 0 || hour > 23;
      if (!this.hourError) {
        this.selectedHour = hour;
        this.autoApply();
      }
    },
    formatHour() {
      if (!this.hourError && this.hourInput) {
        this.hourInput = String(parseInt(this.hourInput)).padStart(2, "0");
      }
    },
    validateMinute() {
      const minute = parseInt(this.minuteInput);
      this.minuteError = isNaN(minute) || minute < 0 || minute > 59;
      if (!this.minuteError) {
        this.selectedMinute = String(minute).padStart(2, "0");
        this.autoApply();
      }
    },
    formatMinute() {
      if (!this.minuteError && this.minuteInput) {
        this.minuteInput = String(parseInt(this.minuteInput)).padStart(2, "0");
      }
    },
    applySettings({ closeOnSuccess = false } = {}) {
      // Validate inputs before submitting
      this.validateHour();
      this.validateMinute();

      if (this.hourError || this.minuteError) {
        this.errorMessage = "Please enter valid time values.";
        return;
      }

      this.isSettingsApplied = true;
      let time = `${parseInt(this.selectedHour)}-${parseInt(
        this.selectedMinute
      )}`;
      let formattedSettings = "";

      if (
        (this.repeatOption === "every-week" ||
          this.repeatOption === "every-bi-week") &&
        this.selectedWeekDays.length === 0
      ) {
        this.errorMessage = "Please select at least one day of the week.";
        return;
      } else {
        this.errorMessage = "";
      }

      if (
        this.repeatOption === "every-month" &&
        (this.monthDay < 1 || this.monthDay > 31)
      ) {
        this.errorMessage = "Day of the month must be between 1 and 31.";
        return;
      }

      switch (this.repeatOption) {
        case "every-x-days": {
          formattedSettings = `every-day-${time}`;
          break;
        }
        case "every-week": {
          const selectedDays = this.selectedWeekDays
            .map((day) => day.toLowerCase().substring(0, 3))
            .join("-");
          formattedSettings = `every-week-${time}-${selectedDays}`;
          break;
        }
        case "every-bi-week": {
          const selectedDays = this.selectedWeekDays
            .map((day) => day.toLowerCase().substring(0, 3))
            .join("-");

          formattedSettings = `every-bi-week-time-${this.selectedHour}-${this.selectedMinute}-days-${selectedDays}`;
          break;
        }
        case "every-month": {
          formattedSettings = `every-month-${time}-${this.monthDay}`;
          break;
        }
        default:
          break;
      }

      this.$emit("settingsApplied", formattedSettings);
      if (closeOnSuccess) {
        this.isWindowOpen = false;
        this.$emit("isWindowOpen", false);
      }
    },
  },
};
</script>

<style scoped>
.scheduler-container {
  position: absolute;
  z-index: 50;
  width: 100%;
  max-width: 26rem;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border-radius: 1.25rem;
  overflow: hidden;
}

@media (max-width: 780px) {
  .scheduler-container {
    max-width: min(90vw, 24rem);
    left: 0;
    transform: none;
  }
}
</style>
